import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, Platform, Pressable, StyleSheet, TextInput, View } from "react-native";
import {
  BookOpenIcon,
  CaretDownIcon,
  CheckCircleIcon,
  FilePlusIcon,
  PlusIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import CustomText from "../../common/general/CustomText/CustomText";
import CourseModernizerOutcomeItem from "../CourseModernizerOutcomeItem/CourseModernizerOutcomeItem";
import { colors, spacing, typographyScale } from "../../../styles/design-system";

export type CourseModernizerLearningOutcome = {
  id: string;
  label: string;
  text: string;
  aiBadgeLabel?: string | null;
  canRevertAIRevision?: boolean;
};

type CourseModernizerLearningOutcomesProps = {
  outcomes: CourseModernizerLearningOutcome[];
  approved: boolean;
  busy?: boolean;
  sourceStatus?: CourseModernizerSourceStatus | null;
  sourceMessage?: string | null;
  onAddManual: (text: string) => Promise<void> | void;
  onGenerateWithAI: (instruction: string) => Promise<void> | void;
  onUploadPdfOrDoc: () => void;
  onDropFiles?: (files: File[]) => void;
  onApprove: (approveEmpty: boolean) => void;
  onSaveOutcome: (outcomeId: string, text: string) => Promise<void> | void;
  onDeleteOutcome: (outcomeId: string) => Promise<void> | void;
  onReviseOutcome: (outcomeId: string, instruction: string, selectedOptions: string[]) => Promise<void> | void;
  onRevertOutcome: (outcomeId: string) => Promise<void> | void;
};

type CourseModernizerSourceStatus = "idle" | "loading" | "empty" | "error";

const AI_GUIDANCE_CHIPS = [
  "Focus on quantitative skills",
  "Strong writing and communication",
  "Research and inquiry skills",
  "Applied / career-ready",
];

function CourseModernizerLearningOutcomes({
  outcomes,
  approved,
  busy = false,
  sourceStatus,
  sourceMessage,
  onAddManual,
  onGenerateWithAI,
  onUploadPdfOrDoc,
  onDropFiles,
  onApprove,
  onSaveOutcome,
  onDeleteOutcome,
  onReviseOutcome,
  onRevertOutcome,
}: CourseModernizerLearningOutcomesProps) {
  const addMenuAnchorRef = useRef<React.ElementRef<typeof View> | null>(null);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const hasOutcomes = outcomes.length > 0;

  useEffect(() => {
    if (!isAddMenuOpen || typeof document === "undefined") return undefined;

    const handleOutsidePress = (event: MouseEvent | TouchEvent) => {
      const anchorNode = addMenuAnchorRef.current as unknown as { contains?: (node: Node) => boolean } | null;
      if (!anchorNode?.contains || !(event.target instanceof Node)) {
        setIsAddMenuOpen(false);
        return;
      }
      if (!anchorNode.contains?.(event.target)) {
        setIsAddMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsidePress, true);
    document.addEventListener("touchstart", handleOutsidePress, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsidePress, true);
      document.removeEventListener("touchstart", handleOutsidePress, true);
    };
  }, [isAddMenuOpen]);

  const handleMenuAction = (action: () => Promise<void> | void) => {
    void action();
    setIsAddMenuOpen(false);
  };

  const addEmptyManualOutcome = async () => {
    try {
      await onAddManual("");
    } catch {
      // Parent handles the user-facing error; keep the menu closed after the failed action.
    }
  };

  const openAiPanel = () => {
    setAiInstruction("");
    setIsAiPanelOpen(true);
    setIsAddMenuOpen(false);
  };

  const closeAiPanel = () => {
    setAiInstruction("");
    setIsAiPanelOpen(false);
  };

  const submitAiPanel = async () => {
    const instruction = aiInstruction.trim();
    await onGenerateWithAI(instruction);
    closeAiPanel();
  };

  // -- Drag-and-drop handlers (web only) --
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length > 0 && onDropFiles) {
        onDropFiles(files);
      }
    },
    [onDropFiles]
  );

  const isWeb = Platform.OS === "web";
  const DropzoneWrapper = isWeb ? "div" : View;
  const dropzoneProps = isWeb
    ? {
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        style: {
          position: "relative" as const,
        },
      }
    : {};

  return (
    <DropzoneWrapper {...(dropzoneProps as any)}>
      {isDragging && isWeb ? (
        <View style={styles.dropOverlay}>
          <FilePlusIcon color="#003CB3" size={28} weight="duotone" />
          <CustomText
            text="Drop PDF, DOC, or DOCX to extract outcomes"
            size="s"
            weight="bold"
            useTranslationText={false}
            style={styles.dropOverlayText}
          />
        </View>
      ) : null}
      <View style={[styles.panel, isAddMenuOpen && styles.panelMenuOpen]}>
        <View style={styles.headerRow}>
          <View style={styles.headerCopy}>
            <CustomText
              text="Learning outcomes"
              size="l"
              weight="bold"
              role="heading"
              aria-level="2"
              useTranslationText={false}
              style={styles.title}
            />
            {hasOutcomes ? (
              <CustomText
                text="Pulled from the existing course. Add, remove, refine — or regenerate the whole set with AI. Approve when ready"
                size="s"
                useTranslationText={false}
                style={styles.description}
              />
            ) : null}
          </View>
        </View>

        {hasOutcomes ? (
          <View style={styles.outcomeList}>
            {outcomes.map((outcome) => (
              <CourseModernizerOutcomeItem
                key={outcome.id}
                label={outcome.label}
                text={outcome.text}
                density="compact"
                disabled={busy}
                onSave={(text) => onSaveOutcome(outcome.id, text)}
                onDelete={() => onDeleteOutcome(outcome.id)}
                onReviseWithAI={(instruction, selectedOptions) =>
                  onReviseOutcome(outcome.id, instruction, selectedOptions)
                }
                onRevertAIRevision={() => onRevertOutcome(outcome.id)}
                aiBadgeLabel={outcome.aiBadgeLabel}
                canRevertAIRevision={outcome.canRevertAIRevision}
              />
            ))}
          </View>
        ) : (
          <>
            <View style={styles.emptyState}>
              <BookOpenIcon color="#64748B" size={23} />
              <CustomText
                text="We didn't detect any learning objectives for this course. Use the Add outcome menu below to add them manually, generate a full set with AI, or extract them from a syllabus or program document."
                size="s"
                useTranslationText={false}
                style={styles.emptyText}
              />
            </View>
          </>
        )}

        {sourceMessage ? <SourceStatusCallout message={sourceMessage} status={sourceStatus} /> : null}

        {isAiPanelOpen ? (
          <View style={styles.aiGuidePanel}>
            <CustomText
              text="GUIDE THE AI — WHAT SHOULD THESE OUTCOMES EMPHASIZE?"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.aiGuideTitle}
            />
            <View style={styles.aiChipRow}>
              {AI_GUIDANCE_CHIPS.map((chip) => {
                const selected = aiInstruction.trim() === chip;
                return (
                  <Pressable
                    key={chip}
                    accessibilityRole="button"
                    onPress={() => setAiInstruction(chip)}
                    style={[styles.aiGuideChip, selected && styles.aiGuideChipSelected]}>
                    <CustomText
                      text={chip}
                      size="xs"
                      useTranslationText={false}
                      style={[styles.aiGuideChipText, selected && styles.aiGuideChipTextSelected]}
                    />
                  </Pressable>
                );
              })}
            </View>
            <TextInput
              accessibilityLabel="AI outcome generation instruction"
              multiline
              value={aiInstruction}
              onChangeText={setAiInstruction}
              placeholder='Describe the slant — e.g., "emphasize critical thinking and analysis of primary sources, with a final research project"'
              placeholderTextColor={colors.grey[500]}
              style={styles.aiInstructionInput}
            />
            <CustomText
              text={
                hasOutcomes
                  ? "Replaces the current list with 5 AI-generated outcomes. You can edit any row after generation."
                  : "Drafts 5 editable outcome candidates from the available course context."
              }
              size="xs"
              useTranslationText={false}
              style={styles.aiGuideHint}
            />
            <View style={styles.aiGuideActions}>
              <Pressable
                accessibilityRole="button"
                disabled={busy}
                onPress={closeAiPanel}
                style={styles.aiCancelButton}>
                <CustomText text="×" size="s" useTranslationText={false} style={styles.aiCancelIcon} />
                <CustomText text="Cancel" size="s" useTranslationText={false} style={styles.aiCancelText} />
              </Pressable>
              <Pressable
                accessibilityRole="button"
                disabled={busy}
                onPress={() => void submitAiPanel()}
                style={[styles.aiGenerateButton, busy && styles.disabled]}>
                <SparkleIcon color="#334155" size={14} weight="bold" />
                <CustomText
                  text={busy ? "Generating..." : "Generate outcomes"}
                  size="s"
                  useTranslationText={false}
                  style={styles.aiGenerateText}
                />
              </Pressable>
            </View>
          </View>
        ) : null}

        <View style={styles.footerRow}>
          <View ref={addMenuAnchorRef} style={styles.addMenuAnchor}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Add outcome"
              accessibilityState={{ disabled: busy }}
              disabled={busy}
              onPress={() => setIsAddMenuOpen((value) => !value)}
              style={[styles.addButton, busy && styles.disabled]}>
              <PlusIcon color="#003CB3" size={14} weight="bold" />
              <CustomText text="Add outcome" size="s" useTranslationText={false} style={styles.addText} />
              <CaretDownIcon color="#003CB3" size={12} />
            </Pressable>
            {isAddMenuOpen ? (
              <View style={styles.addMenu}>
                <MenuAction
                  label="Add manually"
                  subtitle="Type the outcome yourself"
                  onPress={() => handleMenuAction(addEmptyManualOutcome)}
                  disabled={busy}
                  icon={<PlusIcon size={17} color={colors.grey[700]} weight="regular" />}
                />
                <MenuAction
                  label="Generate full set with AI"
                  subtitle="Describe the slant, AI drafts 5 outcomes"
                  onPress={openAiPanel}
                  icon={<SparkleIcon size={17} color={colors.brand[500]} weight="regular" />}
                />
                <MenuAction
                  label="Upload PDF or DOC"
                  subtitle="Extract outcomes from a syllabus or program doc"
                  onPress={() => handleMenuAction(onUploadPdfOrDoc)}
                  disabled={busy}
                  icon={<FilePlusIcon size={17} color={colors.grey[700]} weight="regular" />}
                />
              </View>
            ) : null}
          </View>

          {approved ? (
            <View style={styles.approvedPill}>
              <View style={styles.approvedMessage}>
                <CheckCircleIcon color="#3B8A13" size={14} weight="regular" />
                <CustomText text="Outcomes approved" size="s" useTranslationText={false} style={styles.approvedText} />
              </View>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={hasOutcomes ? "Approve outcomes" : "Approve no outcomes"}
              disabled={busy}
              onPress={() => onApprove(!hasOutcomes)}
              style={[styles.approveButton, busy && styles.disabled]}>
              <CheckCircleIcon color="#FFFFFF" size={14} weight="bold" />
              <CustomText
                text={hasOutcomes ? "Approve outcomes" : "Approve — no outcomes"}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.approveText}
              />
            </Pressable>
          )}
        </View>

        {!approved ? (
          <CustomText
            text="Modernize is enabled once you approve the final outcome set. Edits reset approval — re-approve after every change."
            size="xs"
            useTranslationText={false}
            style={styles.footerHint}
          />
        ) : null}
      </View>
    </DropzoneWrapper>
  );
}

function SourceStatusCallout({ message, status }: { message: string; status?: CourseModernizerSourceStatus | null }) {
  const isLoading = status === "loading";
  const isError = status === "error";
  const title = isLoading ? "Generating learning outcomes" : isError ? "Learning outcome source update failed" : null;

  return (
    <View
      accessibilityRole={isLoading ? "progressbar" : undefined}
      accessibilityLiveRegion={isLoading ? "polite" : undefined}
      accessibilityLabel={isLoading ? "Learning outcome generation status" : undefined}
      style={[styles.sourceState, isLoading && styles.sourceStateLoading, isError && styles.sourceStateError]}>
      {isLoading ? <SourceStatusSpinner /> : null}
      <View style={styles.sourceStatusCopy}>
        {title ? (
          <CustomText
            text={title}
            size="xs"
            weight="bold"
            useTranslationText={false}
            style={[styles.sourceStatusTitle, isError && styles.sourceStatusErrorTitle]}
          />
        ) : null}
        <CustomText
          text={message}
          size="s"
          useTranslationText={false}
          style={[styles.sourceStatusMessage, isError && styles.sourceStatusErrorMessage]}
        />
      </View>
    </View>
  );
}

function SourceStatusSpinner() {
  return (
    <View style={styles.sourceStatusIcon}>
      <ActivityIndicator accessibilityLabel="Learning outcome generation in progress" color="#3E68FE" size="small" />
    </View>
  );
}

function MenuAction({
  disabled,
  icon,
  label,
  onPress,
  subtitle,
}: {
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
  subtitle: string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      accessibilityRole="menuitem"
      accessibilityLabel={label}
      accessibilityState={{ disabled: !!disabled }}
      disabled={disabled}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onPress}
      style={[styles.menuAction, isHovered && !disabled && styles.menuActionHovered, disabled && styles.disabled]}>
      {icon}
      <View style={styles.menuActionCopy}>
        <CustomText text={label} size="s" useTranslationText={false} style={styles.menuText} />
        <CustomText text={subtitle} size="xs" useTranslationText={false} style={styles.menuSubtext} />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 14,
    padding: 22,
    gap: 14,
    position: "relative",
    zIndex: 1,
  },
  panelMenuOpen: {
    zIndex: 50,
    elevation: 12,
  },
  dropOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(219, 234, 254, 0.9)",
    borderWidth: 2,
    borderColor: "#003CB3",
    borderStyle: "dashed",
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    zIndex: 100,
  },
  dropOverlayText: {
    color: "#003CB3",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
  },
  headerCopy: {
    flex: 1,
    gap: 5,
  },
  title: {
    color: "#020617",
  },
  description: {
    color: "#0F1F44",
    lineHeight: 20,
  },
  outcomeList: {
    gap: 10,
  },
  emptyState: {
    minHeight: 137,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderStyle: "dashed",
    borderRadius: 7,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    paddingHorizontal: 70,
  },
  emptyText: {
    color: "#0F1F44",
    textAlign: "center",
    lineHeight: 22,
    maxWidth: 460,
  },
  sourceState: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: "#F8FAFC",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: 12,
  },
  sourceStateLoading: {
    backgroundColor: "#F5F8FF",
    borderColor: "#D1E0FF",
  },
  sourceStateError: {
    backgroundColor: "#FEF3F2",
    borderColor: "#FECDCA",
  },
  sourceStatusIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#E0EAFF",
    flexShrink: 0,
  },
  sourceStatusCopy: {
    flex: 1,
    gap: 2,
  },
  sourceStatusTitle: {
    color: "#175CD3",
    fontSize: 11,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  sourceStatusMessage: {
    color: "#0F1F44",
    lineHeight: 20,
  },
  sourceStatusErrorTitle: {
    color: "#B42318",
  },
  sourceStatusErrorMessage: {
    color: "#B42318",
  },
  aiGuidePanel: {
    backgroundColor: "#F3F6FF",
    borderWidth: 1,
    borderColor: "#DBE4F5",
    borderRadius: 7,
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 12,
  },
  aiGuideTitle: {
    color: colors.blue[700],
    fontSize: 11,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  aiChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  aiGuideChip: {
    minHeight: 28,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 999,
    backgroundColor: colors.white,
    paddingHorizontal: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  aiGuideChipSelected: {
    backgroundColor: "#DDE7FF",
    borderColor: "#C9D7FF",
  },
  aiGuideChipText: {
    color: colors.blue[700],
    fontSize: 12,
  },
  aiGuideChipTextSelected: {
    color: "#003CB3",
  },
  aiInstructionInput: {
    minHeight: 52,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 4,
    backgroundColor: colors.white,
    paddingHorizontal: 10,
    paddingVertical: 9,
    color: "#0F1F44",
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    textAlignVertical: "top",
  },
  aiGuideHint: {
    color: "#64748B",
    lineHeight: typographyScale.textXs.lineHeight,
  },
  aiGuideActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 16,
  },
  aiCancelButton: {
    minHeight: 32,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  aiCancelIcon: {
    color: "#64748B",
    lineHeight: typographyScale.textSm.lineHeight,
  },
  aiCancelText: {
    color: "#334155",
    fontSize: 12,
  },
  aiGenerateButton: {
    minHeight: 34,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 6,
    backgroundColor: colors.white,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    shadowColor: colors.black,
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  aiGenerateText: {
    color: "#334155",
    fontSize: 12,
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    zIndex: 2,
  },
  addMenuAnchor: {
    position: "relative",
    zIndex: 60,
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    minHeight: 28,
    paddingHorizontal: 8,
  },
  addText: {
    color: colors.blue[600],
    fontSize: 12,
  },
  addMenu: {
    position: "absolute",
    top: 32,
    left: 0,
    zIndex: 70,
    elevation: 14,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 8,
    padding: 5,
    width: 320,
    shadowColor: colors.black,
    shadowOpacity: 0.12,
    shadowRadius: 12,
  },
  menuAction: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 11,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  menuActionHovered: {
    backgroundColor: colors.grey[100],
  },
  menuActionCopy: {
    flex: 1,
    gap: 2,
  },
  menuText: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  menuSubtext: {
    color: colors.grey[500],
    lineHeight: typographyScale.textXs.lineHeight,
  },
  approveButton: {
    minHeight: 34,
    borderRadius: 6,
    backgroundColor: "#1D4ED8",
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
  },
  approveText: {
    color: colors.white,
    fontSize: 12,
  },
  approvedPill: {
    minHeight: 33,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#D9F7BE",
    backgroundColor: "#F2FDEB",
    paddingHorizontal: 11,
    flexDirection: "row",
    alignItems: "center",
    gap: 20,
  },
  approvedMessage: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  approvedText: {
    color: "#3B8A13",
    fontSize: 14,
  },
  footerHint: {
    color: "#64748B",
    lineHeight: 18,
  },
  disabled: {
    opacity: 0.5,
  },
});

export default CourseModernizerLearningOutcomes;
